import React from 'react';
import type { AppointmentType } from '../../../contexts/PsychologistContext';
import { SESSION_APPOINTMENT_TYPES } from '../../../hooks/api';
import { isImpersonated } from '../../../hooks/api/auth/tokenManager';
import CalendarImg from '../../../assets/Calendar.png';
import CircularProgress from '../../common/CircularProgress';

type Patient = {
  name?: string;
  email: string;
  phone?: string;
  timezone?: string;
  nationality?: string;
  process_type?: string;
  process?: string;
  processType?: string;
  clinical_service_id?: string | number;
  clinicalServiceId?: string | number;
  hasNextSessionDate?: boolean;
  hasPriorSessions?: boolean;
  schedulingEligible?: boolean;
};

export const getServiceNameFromId = (ServiceId: string | null | undefined): string => {
  if (!ServiceId) return 'Sin id';
  
  const appointmentType = SESSION_APPOINTMENT_TYPES.find(type => type.clinical_service === ServiceId);
  if (!appointmentType) return ServiceId;
  
  return appointmentType.name;
};

type Props = {
  open: boolean;
  onClose: () => void;
  patients: Patient[];
  appointmentTypes: AppointmentType[];
  selectedPatientEmail: string;
  selectedProcessId?: string;
  selectedAppointmentTypeId: string;
  onChangePatient: (email: string, processId?: string) => void;
  onChangeAppointmentType: (typeId: string) => void;
  onConfirm: () => void;
  mode: 'schedule' | 'recurring';
  initialQuery?: string;
  patientsLoading?: boolean;
};

const SelectPatientAndTypeModal: React.FC<Props> = ({
  open,
  onClose,
  patients,
  appointmentTypes,
  selectedPatientEmail,
  selectedProcessId,
  selectedAppointmentTypeId: _selectedAppointmentTypeId,
  onChangePatient,
  onChangeAppointmentType,
  onConfirm,
  mode,
  initialQuery,
  patientsLoading = false,
}) => {
  const patientSelected = Boolean(selectedPatientEmail);
  const canConfirm = Boolean(patientSelected);

  const [query, setQuery] = React.useState<string>('');
  const [isFocused, setIsFocused] = React.useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] = React.useState<number>(-1);

  // Un paciente puede tener varios procesos con la misma psicóloga (ej: Individual y Pareja),
  // todos con el mismo email: el proceso es lo que identifica de forma única a la opción elegida.
  const findSelected = React.useCallback(
    (list: Patient[]) => list.find(p => (selectedProcessId ? p.process === selectedProcessId : p.email === selectedPatientEmail)),
    [selectedProcessId, selectedPatientEmail]
  );

  React.useEffect(() => {
    if (!selectedPatientEmail) {
      // Si hay initialQuery y no hay paciente seleccionado, pre-cargar la búsqueda
      if (initialQuery) {
        setQuery(initialQuery);
      } else {
        setQuery('');
      }
      return;
    }
    const found = findSelected(patients);
    setQuery(found?.name || found?.email || selectedPatientEmail);
  }, [selectedPatientEmail, patients, initialQuery, findSelected]);

  // Solo pacientes que ya tuvieron al menos una sesión con esta psicóloga.
  // CX impersonando: se levanta la restricción para poder agendar la primera sesión.
  const impersonating = React.useMemo(() => isImpersonated(), []);
  const eligiblePatients = React.useMemo(
    () => (impersonating ? patients : patients.filter(p => p.hasPriorSessions !== false))
      .filter(p => p.schedulingEligible !== false),
    [patients, impersonating]
  );

  const normalize = React.useCallback((s: string) =>
    (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
  , []);
  const filtered = React.useMemo(() => {
    const q = normalize(query);
    if (!q) return [];
    const seen = new Set<string>();
    const results = eligiblePatients.filter(p => {
      const name = normalize(p.name || '');
      const email = normalize(p.email || '');
      const match = name.includes(q) || email.includes(q);
      if (!match) return false;
      const key = p.process || normalize((p.name || p.email || '').trim());
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return results.slice(0, 20);
  }, [eligiblePatients, query, normalize]);
  const focusResults = React.useMemo(() => {
    if (!isFocused) return [];
    const seen = new Set<string>();
    const results = eligiblePatients.filter(p => {
      const key = p.process || normalize((p.name || p.email || '').trim());
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return results;
  }, [eligiblePatients, isFocused, normalize]);
  const resultsToShow = (query.trim().length > 0 ? filtered : focusResults);

  // Reset highlight when results change
  React.useEffect(() => { setHighlightedIndex(-1); }, [resultsToShow.length]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (resultsToShow.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(i => Math.min(i + 1, resultsToShow.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const idx = highlightedIndex >= 0 ? highlightedIndex : 0;
      const p = resultsToShow[idx];
      if (p) { onChangePatient(p.email, p.process); setQuery(p.name || p.email); setTimeout(() => onConfirm(), 0); }
    } else if (e.key === 'Escape') {
      setIsFocused(false);
    }
  };

  // Auto-seleccionar appointment type según clinical_service_id o process_type/name
  React.useEffect(() => {
    if (!selectedPatientEmail) return;
    const p = findSelected(patients);
    if (!p || !Array.isArray(appointmentTypes) || appointmentTypes.length === 0) return;

    const clinicalId = (p as unknown as { clinical_service_id?: unknown; clinicalServiceId?: unknown }).clinical_service_id 
      ?? (p as unknown as { clinicalServiceId?: unknown }).clinicalServiceId;
    let match = undefined as AppointmentType | undefined;

    if (clinicalId != null) {
      match = appointmentTypes.find(t => String(t.id) === String(clinicalId));
    }
    if (!match) {
      const ptName = String((p as unknown as { process_type?: string; processType?: string }).process_type 
        ?? (p as unknown as { processType?: string }).processType 
        ?? '').toLowerCase().trim();
      if (ptName) {
        match = appointmentTypes.find(t => t.name.toLowerCase() === ptName) 
          || appointmentTypes.find(t => t.name.toLowerCase().includes(ptName));
      }
    }
    if (match) {
      onChangeAppointmentType(match.id);
    }
  }, [selectedPatientEmail, patients, appointmentTypes, onChangeAppointmentType, findSelected]);

  if (!open) return null;
  
  return (
    <>
    <style>{`
      .psi-phone-tip-text {
        visibility: hidden;
        opacity: 0;
        position: fixed;
        background: #1f2937;
        color: #fff;
        font-size: 0.75rem;
        font-weight: 600;
        padding: 4px 8px;
        border-radius: 6px;
        white-space: nowrap;
        pointer-events: none;
        transition: opacity 0.12s ease, visibility 0.12s ease;
        z-index: 9999;
      }
      .psi-phone-tip:hover .psi-phone-tip-text {
        visibility: visible;
        opacity: 1;
      }
    `}</style>
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        direction: 'ltr', 
        zIndex: 1000 
      }} 
      onClick={() => {
        try {
          const lock = (window as unknown as { __psiTourLockOverlay?: boolean }).__psiTourLockOverlay === true;
          if (!lock) onClose();
        } catch { onClose(); }
      }}
    >
      <div
        id="select-patient-type-modal-content"
        className="modal-content" 
        style={{ 
          background: '#fff', 
          borderRadius: '12px', 
          width: '34rem', 
          maxWidth: '90vw', 
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)' 
        }} 
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          padding: '1.5rem 1.5rem 1rem 1.5rem'
        }}>
          <h3 style={{ 
            margin: 0, 
            fontSize: '1.25rem !important', 
            fontWeight: 800, 
            textAlign: 'center', 
            color: '#111827', 
            width: '100%'
          }}>
            Agendar una sesión
          </h3>
          <button 
            style={{ 
              background: 'transparent', 
              border: 0, 
              fontSize: '1.5rem !important', 
              cursor: 'pointer', 
              color: '#6b7280', 
              padding: 0, 
              width: '2rem', 
              height: '2rem', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              borderRadius: '6px', 
              transition: 'background 0.2s' 
            }} 
            onClick={() => {
              const isTourActive = (window as unknown as { __psiTourLockOverlay?: boolean }).__psiTourLockOverlay === true;
              if (!isTourActive) onClose();
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            ×
          </button>
        </div>
        
        {patientsLoading && eligiblePatients.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '7rem', padding: '1.5rem' }}>
            <CircularProgress sizeRem={2} label="Cargando pacientes..." />
          </div>
        ) : eligiblePatients.length === 0 && !patientsLoading && !(typeof window !== 'undefined' && (window as unknown as { __psiTourLockOverlay?: boolean }).__psiTourLockOverlay === true) ? (
          <div style={{
            padding: '1.5rem 1.5rem 2rem',
            textAlign: 'center',
            border: '1px solid #e5e7eb',
            borderRadius: 14,
            background: '#fff',
            margin: '0 1.5rem 1.5rem',
          }}>
            <img src={CalendarImg} alt="" style={{ width: 140, height: 'auto', display: 'block', margin: '0 auto 10px' }} />
            <h4 style={{ margin: '0 0 6px', color: '#111827', fontSize: '1rem', fontWeight: 700 }}>
              No hay pacientes disponibles para agendar
            </h4>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '0.85rem', lineHeight: 1.5 }}>
              Todos tus pacientes ya tienen su próxima sesión agendada o aún no tuvieron su primera sesión. Cuando haya pacientes sin próxima sesión agendada, podrás realizarlo desde aquí.
            </p>
          </div>
        ) : (
        <>
        <div style={{ padding: '1.5rem', fontSize: '0.875rem !important', textAlign: 'left' }}>
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '0.875rem !important',
              fontWeight: 700,
              color: '#374151'
            }}>
              Paciente
            </label>
            <div id="select-patient-search-block" style={{ position: 'relative' }}>
              <input
                id="select-patient-input"
                type="text"
                value={query}
                onChange={(e) => { setQuery(e.target.value); if (selectedPatientEmail) onChangePatient('', ''); }}
                placeholder="Busca un paciente"
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '0.625rem 0.75rem',
                  fontSize: '0.875rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  background: '#fff',
                  transition: 'border 0.2s',
                  outline: 'none'
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#6D57BC'; setIsFocused(true); }}
                onBlur={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; window.setTimeout(() => setIsFocused(false), 120); }}
                onKeyDown={handleKeyDown}
              />
              {(() => {
                const showTourMock = typeof window !== 'undefined' && (window as unknown as { __psiBookingMockPatients?: boolean }).__psiBookingMockPatients === true;
                if (!showTourMock) return null;
                return (
                  <div
                    style={{
                      marginTop: '0.5rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      maxHeight: '7.5rem',
                      overflowY: 'auto',
                      background: '#fff'
                    }}
                    aria-label="Resultados de pacientes (demo)"
                  >
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); onChangePatient('ana.lopez@demo.psi', 'mock-process-individual'); setQuery('Ana López'); }}
                      style={{
                        display: 'flex',
                        width: '100%',
                        textAlign: 'left',
                        gap: '0.5rem',
                        padding: '0.5rem 0.75rem',
                        background: selectedPatientEmail === 'ana.lopez@demo.psi' ? '#f3f4f6' : '#fff',
                        border: 0,
                        borderBottom: '1px solid #f3f4f6',
                        cursor: 'pointer'
                      }}
                    >
                      <span style={{ fontWeight: 700, color: '#111827', flex: 1 }}>Ana López</span>
                      <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>Individual</span>
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); onChangePatient('carlos.ruiz@demo.psi', 'mock-process-pareja'); setQuery('Carlos Ruiz'); }}
                      style={{
                        display: 'flex',
                        width: '100%',
                        textAlign: 'left',
                        gap: '0.5rem',
                        padding: '0.5rem 0.75rem',
                        background: selectedPatientEmail === 'carlos.ruiz@demo.psi' ? '#f3f4f6' : '#fff',
                        border: 0,
                        borderBottom: '1px solid #f3f4f6',
                        cursor: 'pointer'
                      }}
                    >
                      <span style={{ fontWeight: 700, color: '#111827', flex: 1 }}>Carlos Ruiz</span>
                      <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>Pareja</span>
                    </button>
                  </div>
                );
              })()}
              {resultsToShow.length > 0 && (
                <div
                  style={{
                    marginTop: '0.5rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    maxHeight: '7.5rem', /* aprox 3 items visibles */
                    overflowY: 'auto',
                    background: '#fff'
                  }}
                  role="listbox"
                  aria-label="Resultados de pacientes"
                >
                  {resultsToShow.map((p, idx) => (
                    <button
                      key={p.process || p.email}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); onChangePatient(p.email, p.process); setQuery(p.name || p.email); setTimeout(() => onConfirm(), 0); }}
                      onMouseEnter={() => setHighlightedIndex(idx)}
                      onMouseLeave={() => setHighlightedIndex(-1)}
                      style={{
                        display: 'flex',
                        width: '100%',
                        textAlign: 'left',
                        gap: '0.5rem',
                        padding: '0.5rem 0.75rem',
                        background: highlightedIndex === idx ? '#ede9f8' : (selectedProcessId ? selectedProcessId === p.process : selectedPatientEmail === p.email) ? '#f3f4f6' : '#fff',
                        border: 0,
                        borderBottom: '1px solid #f3f4f6',
                        cursor: 'pointer'
                      }}
                    >
                      <span style={{ fontWeight: 700, color: '#111827', flex: 1, display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
                        {p.name || p.email}
                      </span>
                      {(p.process_type || p.processType) && (
                        <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                          {getServiceNameFromId(p.processType)}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          {/* Radios de modo de agendamiento */}
          <div style={{ display: 'none', justifyContent: 'center', marginTop: '1rem' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontWeight: 700, color: '#374151', marginRight: '1rem' }}>
              <input type="radio" name="appt-mode" checked={mode === 'schedule'} readOnly style={{ accentColor: '#6D57BC' }} />
              Agendamiento único
            </label>
          </div>
        </div>

        <div style={{
          padding: '1rem 1.5rem 1.5rem 1.5rem',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          borderTop: '1px solid #e5e7eb',
          gap: '0.5rem'
        }}>
          <button
            id="select-patient-confirm-btn"
            disabled={!canConfirm}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              border: 0,
              borderRadius: '9999px',
              padding: '0.5rem 0.875rem',
              background: '#6D57BC',
              color: '#ffffff',
              fontWeight: 700,
              height: '35px',
              cursor: canConfirm ? 'pointer' : 'not-allowed',
              opacity: canConfirm ? 1 : 0.7,
              transition: 'all 0.2s ease-out'
            }}
            onClick={onConfirm}
          >
            Confirmar
          </button>
        </div>
        </>
        )}
      </div>
    </div>
    </>
  );
};

export default SelectPatientAndTypeModal;

